// @flow
import { Record, Map } from "immutable"
import _ from "lodash"
import fp from "lodash/fp"
import * as R from "ramda"

import Journey from "common/records/Journey"
import mapperAdditionalServices from "./mapperAdditionalServices"
import mapperInfantsConditions from "common/services/infants/mapperInfantsConditions"
import mapperHoldBagPricing from "./mapperHoldBagPricing"
import mapperDocumentNeed from "common/services/document/mapperDocumentNeed"
import mapperJourney from "common/services/journey/mapperJourney"
import mapperBagParams from "common/services/bags/mapperBagParams"
import {
  BAGS_TYPES,
  hasPaidHandBagsAllowed,
} from "mmb/scenes/AccountBooking/components/Booking/services/bags/tools"
import { INSURANCE_NAMES } from "common/consts/insurance"
import type {
  CheckFlightsApiSettings,
  CheckFlightsApiOutput,
  CheckFlightsFormattedType,
} from "booking/services/reservation/apis/types/checkFlightsApiTypes"
import { hasHiddenFlight } from "common/services/journey/journeyHiddenFlightTools"
import { getAirlines } from "common/services/staticData"
import { calculateHandBagsPrice } from "../../../additionalServices/services/priorityBoarding/services/calculateHandBagsPrice"
import mapperBaggage from "../../../baggage/mapper/mapperBaggage"
import Price from "common/records/Price"
import { BAGS_MARGIN_CONST } from "booking/services/reservation/constants/marginConsts"

export const CheckFlightsFormatted = Record({
  additionalServices: Object, // eslint-disable-line flowtype/no-weak-types
  adultThreshold: null,
  accepted: Object, // eslint-disable-line flowtype/no-weak-types
  bookingToken: "",
  journey: new Journey(),
  shadowJourney: null,
  maxPassengers: 0,
  bagParams: new Map(),
  baggage: null,
  insurancePricing: new Map(),
  holdBagsPricing: {},
  basePrice: 0,
  adultsPrice: 0,
  childrenPrice: 0,
  infantsPrice: 0,
  infantsConditions: new Map(),
  infantsFareSupported: false,
  fee: 0,
  handBagsPricing: new Map(),
  hasPaidHandBagsAllowed: false,
  handBagsWithPrioBoardingAllowed: false,
  documentNeed: new Map(),
  gracePeriodFee: 0,
  flightsChecked: false,
  flightsInvalid: false,
  fees: new Map(),
  hasHiddenFlight: false,
})

const mapperCheckFlights = (
  res: CheckFlightsApiOutput,
  options: CheckFlightsApiSettings,
): CheckFlightsFormattedType => {
  // NOTE!!!
  // This is a terrible idea and only here as a god damn hotfix
  // If I see anyone else doing this shit he's gonna have to explain himself
  // in front of every FE dev eye to eye as a shaming ritual
  const airlines = getAirlines(window.__INITIAL_STATE__)

  const journey = mapperJourney(res, "", false, airlines, _.get(options, "splitster"))

  const additionalServices = mapperAdditionalServices(res.additional_services)
  const rawShadowJourney = mapperJourney(res, "", true, airlines, _.get(options, "splitster"))

  // If some flight has required prio boarding,
  // to ensure cabin baggage, we must order prio boarding
  const handBagsWithPrioBoardingAllowed =
    journey.getFlights().some(flight => flight.priorityBoarding) &&
    fp.has(["priority_boarding", "data"], additionalServices)

  const handBagsPricing = calculateHandBagsPrice(
    res.hand_bags_price,
    handBagsWithPrioBoardingAllowed,
    fp.getOr({}, ["priority_boarding", "data"], additionalServices),
    journey.getFlights(),
  )

  const _hasPaidHandBagsAllowed = hasPaidHandBagsAllowed(handBagsPricing, journey)
  const infantsConditions = mapperInfantsConditions(res.infants_conditions)
  const documentNeed = mapperDocumentNeed(_.get(res, ["document_options", "document_need"]))

  const holdBagsPricing = fp.has("bags_price_split", res)
    ? fp.compose(
        fp.mapValues(
          value =>
            new Price({
              amount: fp.get("amount", value),
              base: fp.get("base", value),
              service: fp.get("service", value),
              serviceFlat: fp.get("service_flat", value),
              merchant: fp.get("merchant", value),
              currency: fp.get("currency", value),
            }),
        ),
        // $FlowFixMe: error revealed by updating Flow and lodash libdef, please fix
        fp.omit("_abp"),
        fp.get("bags_price_split"),
      )(res)
    : fp.mapValues(
        value =>
          new Price({
            amount: value,
            base: value * BAGS_MARGIN_CONST,
            service: value * (1 - BAGS_MARGIN_CONST),
          }),
        mapperHoldBagPricing(res.bags_price).toJS(),
      )

  if (!fp.has(["bags_price_split", "_abp"], res)) {
    Object.assign(holdBagsPricing, { _abp: -1 })
  } else {
    Object.assign(holdBagsPricing, {
      _abp: fp.get(["bags_price_split", "_abp"], res),
    })
  }

  let bagParams = mapperBagParams(
    res.luggage,
    Map(res.bags_restrictions),
    _hasPaidHandBagsAllowed,
    infantsConditions.get("differentHandWeight"),
    infantsConditions.get("handWeight"),
  )

  // Hand bag not defined in luggage parameter
  if (
    !bagParams.get(BAGS_TYPES.HAND).get("length") &&
    journey.getPriorityBoardingFlights().length > 0
  ) {
    // Check if defined in new api with priority
    // Cabin bags are being moved under bags.definitions.hand_bag. Backward compatible solution
    // $FlowFixMe
    const cabinBags = R.hasPath(["bags", "definitions", "hand_bag"], res)
      ? // $FlowFixMe
        R.pathOr([], ["bags", "definitions", "hand_bag"], res).filter(
          bag => bag.category === "cabin_bag",
        )
      : // $FlowFixMe
        R.pathOr([], ["bags", "definitions", "cabin_bag"], res)

    const prioCabin = cabinBags.find(
      bag => R.pathOr([], ["conditions", "is_priority"], bag).length > 0,
    )

    if (prioCabin) {
      // Rewrite it in bag params
      const oldHand = bagParams.get(BAGS_TYPES.HAND)
      const {
        note,
        restrictions: { width, length, weight, height },
      } = prioCabin

      bagParams = bagParams.set(
        BAGS_TYPES.HAND,
        oldHand.merge(oldHand, { note, width, length, weight, height }),
      )
    }
  }

  return new CheckFlightsFormatted({
    additionalServices,
    infantsConditions,
    bagParams,
    baggage: mapperBaggage(res.baggage, journey, additionalServices),
    handBagsPricing,
    documentNeed,
    holdBagsPricing,
    journey,
    shadowJourney:
      journey.getFlights().size < rawShadowJourney.getFlights().size ? rawShadowJourney : null,
    hasPaidHandBagsAllowed: _hasPaidHandBagsAllowed,
    handBagsWithPrioBoardingAllowed,
    adultThreshold: res.adult_threshold,
    bookingToken: res.booking_token,
    fees: Map({
      allFees: _.get(res, ["fees_dropdown", "fees"]),
      baseFare: _.get(res, ["fees_dropdown", "base_fare"]),
      merchantFee: _.get(res, ["fees_dropdown", "merchant_fee"]),
    }),
    fee: _.get(res, ["fees_dropdown", "fees"], res.sp_fee),
    maxPassengers: res.max_passengers,
    insurancePricing: Map({
      [INSURANCE_NAMES.BASIC]: res.insurance_price.travel_basic,
      [INSURANCE_NAMES.PLUS]: res.insurance_price.travel_plus,
      none: 0,
    }),
    basePrice: res.orig_price,
    adultsPrice: res.adults_price,
    childrenPrice: res.children_price,
    infantsPrice: res.infants_price,
    // both infant & adults price must be in response
    infantsFareSupported: !_.isUndefined(res.adults_price) && !_.isUndefined(res.infants_price),
    gracePeriodFee: res.grace_period_fee,
    flightsChecked: res.flights_checked && _.get(res, "transfers_checked", true),
    flightsInvalid: res.flights_invalid || res.transfers_invalid,
    hasHiddenFlight: hasHiddenFlight(res),
  })
}

export default mapperCheckFlights
